import { createModel } from '@rematch/core';
import { RootModel } from '.';
import { IUser } from '../../types/interfaces';
import CONSTANTS from '../../contants';
import { authAPI, LoginRequest, UserProfile } from '../../services/api';

interface IState {
  isLoggedIn: boolean;
  tokenPromise: any;
  email: string;
  user: UserProfile | null;
  hashData: string;
  attesationType: 'text' | 'document';
  loading: boolean;
  error: string | null;
  profileLoading: boolean;
}

export const auth = createModel<RootModel>()({
  state: {
    isLoggedIn: !!localStorage.getItem(CONSTANTS.token),
    tokenPromise: null,
    email: '',
    user: null,
    hashData: '',
    attesationType: 'text',
    loading: false,
    error: null,
    profileLoading: false,
  } as IState,
  reducers: {
    setIsLoggedIn(state, payload: boolean) {
      state.isLoggedIn = payload;
    },
    setTokenPromise(state, payload: any) {
      state.tokenPromise = payload;
    },
    setEmail(state, payload: string) {
      state.email = payload;
    },
    setUser(state, payload: UserProfile | null) {
      state.user = payload;
    },
    setHashData(state, payload: string) {
      state.hashData = payload;
    },
    setAttesationType(state, payload: 'text' | 'document') {
      state.attesationType = payload;
    },
    setLoading(state, payload: boolean) {
      state.loading = payload;
    },
    setError(state, payload: string | null) {
      state.error = payload;
    },
    clearError(state) {
      state.error = null;
    },
    setProfileLoading(state, payload: boolean) {
      state.profileLoading = payload;
    },
  },
  effects: (dispatch) => ({
    async loginAsync(payload: LoginRequest) {
      try {
        dispatch.auth.setLoading(true);
        dispatch.auth.clearError();
        
        const response = await authAPI.login(payload);
        
        // Store token in localStorage
        localStorage.setItem(CONSTANTS.token, response.access_token);
        
        // Update auth state
        dispatch.auth.setIsLoggedIn(true);
        dispatch.auth.setEmail(payload.email);
        
        return { success: true, message: response.message };
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
        dispatch.auth.setError(errorMessage);
        return { success: false, message: errorMessage };
      } finally {
        dispatch.auth.setLoading(false);
      }
    },
    async logoutAsync() {
      localStorage.removeItem(CONSTANTS.token);
      dispatch.auth.setIsLoggedIn(false);
      dispatch.auth.setEmail('');
      dispatch.auth.setUser(null);
      dispatch.auth.clearError();
    },
    async fetchUserProfileAsync() {
      try {
        dispatch.auth.setProfileLoading(true);
        dispatch.auth.clearError();
        
        const response = await authAPI.getUserProfile();
        
        // Update user state with profile data
        dispatch.auth.setUser(response.user);
        dispatch.auth.setEmail(response.user.email);
        
        return { success: true, user: response.user };
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Failed to fetch user profile';
        dispatch.auth.setError(errorMessage);
        return { success: false, message: errorMessage };
      } finally {
        dispatch.auth.setProfileLoading(false);
      }
    },
  }),
});
